/* Velocity Alerts — P3 anomaly surface.
 *
 * Compact alert chip-strip injected into the quotation detail panel.
 * Shows one row per alert with severity colour, type, and message.
 *
 * Empty state → renders nothing (no visual noise on healthy contracts).
 *
 * Props
 *   quotationId — fetches /api/quotations/{id}/velocity-alerts on mount.
 */
import React, { useEffect, useState } from 'react';
import { axiosInstance } from '../App';
import { AlertOctagon, AlertTriangle, Hourglass, CheckCircle2, X } from 'lucide-react';


const TONE = {
  critical: 'bg-rose-100 border-rose-400 text-rose-900',
  high:     'bg-orange-100 border-orange-400 text-orange-900',
  medium:   'bg-amber-100 border-amber-300 text-amber-900',
};
const ICON = {
  critical: AlertOctagon,
  high:     AlertTriangle,
  medium:   Hourglass,
};


const VelocityAlertsStrip = ({ quotationId }) => {
  const [data, setData] = useState(null);

  const refresh = async () => {
    if (!quotationId) return;
    try {
      const r = await axiosInstance.get(
        `/quotations/${quotationId}/velocity-alerts`,
      );
      setData(r.data || null);
    } catch (_) {
      setData(null);
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [quotationId]);

  const onAck = async (alert, action) => {
    try {
      await axiosInstance.post(`/alerts/${alert.alert_id}/acknowledge`, {
        action,
        note: '',
        alert_snapshot: alert,
      });
      await refresh();
    } catch (_) { /* swallow — UI stays stable */ }
  };

  if (!data || !data.alerts || data.alerts.length === 0) return null;
  // Hide resolved / dismissed alerts in this strip — the inbox can show the
  // history; on the quotation detail we only surface live work.
  const visible = data.alerts.filter(
    (a) => !["resolved", "dismissed"].includes(a.ack_status)
  );
  if (visible.length === 0) return null;

  return (
    <div data-testid="velocity-alerts-strip" className="mb-3 space-y-1.5">
      {visible.map((a) => {
        const Icon = ICON[a.severity] || AlertTriangle;
        const tone = TONE[a.severity] || TONE.medium;
        const isAck = a.ack_status === 'acknowledged';
        return (
          <div
            key={a.alert_id}
            data-testid={`velocity-alert-${a.type}`}
            className={`flex items-start gap-2 px-3 py-2 rounded border text-sm ${tone} ${isAck ? 'opacity-70' : ''}`}
          >
            <Icon className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <div className="flex items-center gap-2 flex-wrap text-xs">
                <span className="font-semibold uppercase tracking-wider">
                  {a.severity}
                </span>
                <span className="opacity-75">·</span>
                <span className="capitalize" data-testid={`velocity-alert-type-${a.alert_id}`}>
                  {(a.type || '').replace(/_/g, ' ')}
                </span>
                {isAck && (
                  <span data-testid="velocity-alert-ack-chip"
                        className="ml-1 px-1.5 py-0.5 rounded bg-white/70 border border-current text-[10px]">
                    Acknowledged
                  </span>
                )}
                {a.confidence != null && (
                  <span className="ml-auto text-[10px] opacity-70" data-testid={`velocity-alert-confidence-${a.alert_id}`}>
                    confidence {a.confidence}%
                  </span>
                )}
              </div>
              <div className="text-xs mt-0.5">{a.message}</div>
              {a.expected != null && a.actual != null && (
                <div className="text-[11px] opacity-75 mt-0.5 font-mono">
                  baseline: {a.expected} {a.unit || ''} · current: {a.actual} {a.unit || ''}
                </div>
              )}
            </div>
            <div className="flex flex-col gap-1">
              {!isAck && (
                <button
                  data-testid={`alert-ack-${a.alert_id}`}
                  onClick={() => onAck(a, 'acknowledged')}
                  title="Acknowledge — I've seen this"
                  className="text-[10px] px-2 py-0.5 rounded bg-white/70 hover:bg-white border border-current">
                  <CheckCircle2 className="w-3 h-3 inline" /> Ack
                </button>
              )}
              <button
                data-testid={`alert-resolve-${a.alert_id}`}
                onClick={() => onAck(a, 'resolved')}
                title="Resolved — root cause fixed"
                className="text-[10px] px-2 py-0.5 rounded bg-white/70 hover:bg-white border border-current">
                <X className="w-3 h-3 inline" /> Resolve
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default VelocityAlertsStrip;
